'use client'
import { logger } from '@/lib/logger';

import React, { useRef, useEffect, useState, useCallback } from 'react'

interface SWSWebGPUVisualizerProps {
  audioData?: Float32Array
  neuralActivity?: number[]
  frequencyData?: Float32Array
  width: number
  height: number
  mode?: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial'
  className?: string
}

interface WebGPUContext {
  device: GPUDevice
  context: GPUCanvasContext
  format: GPUTextureFormat
  pipeline: GPURenderPipeline
  vertexBuffer: GPUBuffer
  indexBuffer: GPUBuffer
  uniformBuffer: GPUBuffer
}

class WebGPUVisualizationEngine {
  private canvas: HTMLCanvasElement
  private gpuContext: WebGPUContext | null = null
  private animationFrameId: number | null = null
  private time = 0
  private dataPoints: Float32Array = new Float32Array(1024)
  private neuralWeights: Float32Array = new Float32Array(256)

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
  }

  async initialize(): Promise<void> {
    if (!navigator.gpu) {
      throw new Error('WebGPU not supported')
    }

    const adapter = await navigator.gpu.requestAdapter()
    if (!adapter) {
      throw new Error('No WebGPU adapter found')
    }

    const device = await adapter.requestDevice()
    const context = this.canvas.getContext('webgpu') as GPUCanvasContext
    const format = navigator.gpu.getPreferredCanvasFormat()

    context.configure({
      device,
      format,
      alphaMode: 'premultiplied'
    })

    // Create shader modules
    const vertexShader = device.createShaderModule({
      code: `
        struct VertexOutput {
          @builtin(position) position: vec4f,
          @location(0) color: vec4f,
          @location(1) uv: vec2f,
        }

        @vertex
        fn vertexMain(@location(0) position: vec2f, @location(1) color: vec4f, @location(2) uv: vec2f) -> VertexOutput {
          var output: VertexOutput;
          output.position = vec4f(position, 0.0, 1.0);
          output.color = color;
          output.uv = uv;
          return output;
        }

        @fragment
        fn fragmentMain(@location(0) color: vec4f, @location(1) uv: vec2f) -> @location(0) vec4f {
          return color;
        }
      `
    })

    const fragmentShader = device.createShaderModule({
      code: `
        @fragment
        fn fragmentMain(@location(0) color: vec4f, @location(1) uv: vec2f) -> @location(0) vec4f {
          return color;
        }
      `
    })

    // Create pipeline
    const pipeline = device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: vertexShader,
        entryPoint: 'vertexMain',
        buffers: [{
          arrayStride: 8 * 4, // 2 position + 4 color + 2 uv
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x2' },
            { shaderLocation: 1, offset: 8, format: 'float32x4' },
            { shaderLocation: 2, offset: 24, format: 'float32x2' }
          ]
        }]
      },
      fragment: {
        module: fragmentShader,
        entryPoint: 'fragmentMain',
        targets: [{ format }]
      },
      primitive: { topology: 'triangle-list' }
    })

    // Create buffers
    const vertexBuffer = device.createBuffer({
      size: 1024 * 8 * 4, // Max 1024 vertices
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
    })

    const indexBuffer = device.createBuffer({
      size: 1024 * 2, // Max 1024 indices
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST
    })

    const uniformBuffer = device.createBuffer({
      size: 16, // 4 floats for uniforms
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    })

    this.gpuContext = {
      device,
      context,
      format,
      pipeline,
      vertexBuffer,
      indexBuffer,
      uniformBuffer
    }
  }

  updateData(audioData?: Float32Array, neuralActivity?: number[], frequencyData?: Float32Array): void {
    if (audioData) {
      this.dataPoints.set(audioData.slice(0, 1024))
    }
    if (neuralActivity) {
      this.neuralWeights.set(neuralActivity.slice(0, 256).map(v => v * 2 - 1))
    }
  }

  render(mode: string): void {
    if (!this.gpuContext) return

    const { device, context, pipeline, vertexBuffer, uniformBuffer } = this.gpuContext
    const commandEncoder = device.createCommandEncoder()
    const textureView = context.getCurrentTexture().createView()

    const renderPass = commandEncoder.beginRenderPass({
      colorAttachments: [{
        view: textureView,
        clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 },
        loadOp: 'clear',
        storeOp: 'store'
      }]
    })

    renderPass.setPipeline(pipeline)

    // Generate vertices based on mode
    const vertices = this.generateVertices(mode)
    device.queue.writeBuffer(vertexBuffer, 0, vertices as any)

    // Update uniforms
    const uniforms = new Float32Array([this.time, Math.sin(this.time), Math.cos(this.time), 1.0])
    device.queue.writeBuffer(uniformBuffer, 0, uniforms)

    renderPass.setVertexBuffer(0, vertexBuffer)
    renderPass.draw(vertices.length / 8) // 8 floats per vertex
    renderPass.end()

    device.queue.submit([commandEncoder.finish()])
    this.time += 0.016 // ~60fps
  }

  private generateVertices(mode: string): Float32Array {
    const vertices: number[] = []

    switch (mode) {
      case 'waveform':
        return this.generateWaveformVertices()
      case 'spectrum':
        return this.generateSpectrumVertices()
      case 'neural':
        return this.generateNeuralVertices()
      case 'particles':
        return this.generateParticleVertices()
      case 'spatial':
        return this.generateSpatialVertices()
      default:
        return this.generateWaveformVertices()
    }
  }

  private generateWaveformVertices(): Float32Array {
    const vertices: number[] = []

    for (let i = 0; i < Math.min(this.dataPoints.length, 512); i++) {
      const x = (i / 512) * 2 - 1
      const y = this.dataPoints[i] || 0

      // Line strip
      const r = Math.abs(y) * 0.5 + 0.5
      const g = 0.2
      const b = 1.0 - Math.abs(y) * 0.5

      vertices.push(x, y, r, g, b, 1.0, i / 512, 0.5)
    }

    return new Float32Array(vertices)
  }

  private generateSpectrumVertices(): Float32Array {
    const vertices: number[] = []
    const bins = Math.min(this.dataPoints.length, 256)

    for (let i = 0; i < bins; i++) {
      const x = (i / bins) * 2 - 1
      const height = Math.abs(this.dataPoints[i] || 0) * 2

      // Spectrum bars
      const freq = i / bins
      const r = freq
      const g = 1.0 - freq
      const b = Math.sin(freq * Math.PI) * 0.5 + 0.5

      // Bottom vertex
      vertices.push(x - 0.005, -1.0, r * 0.3, g * 0.3, b * 0.3, 1.0, 0, 0)
      // Top vertex
      vertices.push(x + 0.005, -1.0 + height, r, g, b, 1.0, 0, 1)
      vertices.push(x - 0.005, -1.0 + height, r, g, b, 1.0, 1, 1)
    }

    return new Float32Array(vertices)
  }

  private generateNeuralVertices(): Float32Array {
    const vertices: number[] = []

    for (let i = 0; i < Math.min(this.neuralWeights.length, 128); i++) {
      const x = (i / 128) * 2 - 1
      const y = this.neuralWeights[i] || 0

      // Neural network visualization
      const activity = Math.abs(y)
      const r = activity
      const g = 1.0 - activity * 0.5
      const b = activity * 0.3 + 0.7

      vertices.push(x, y, r, g, b, 1.0, i / 128, 0.5)

      // Add connections to nearby neurons
      if (i > 0) {
        const prevY = this.neuralWeights[i - 1] || 0
        const prevX = ((i - 1) / 128) * 2 - 1

        vertices.push(prevX, prevY, r * 0.5, g * 0.5, b * 0.5, 0.5, (i - 1) / 128, 0.5)
        vertices.push(x, y, r * 0.5, g * 0.5, b * 0.5, 0.5, i / 128, 0.5)
      }
    }

    return new Float32Array(vertices)
  }

  private generateParticleVertices(): Float32Array {
    const vertices: number[] = []
    const particleCount = 200

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2
      const radius = 0.1 + Math.sin(this.time * 2 + i * 0.1) * 0.3
      const x = Math.cos(angle) * radius
      const y = Math.sin(angle) * radius

      // Particle color based on audio data
      const dataIndex = Math.floor((i / particleCount) * this.dataPoints.length)
      const intensity = Math.abs(this.dataPoints[dataIndex] || 0)

      const r = intensity * 0.8 + 0.2
      const g = 0.3 + intensity * 0.4
      const b = 0.8 - intensity * 0.3

      vertices.push(x, y, r, g, b, intensity * 0.8 + 0.2, i / particleCount, 0.5)
    }

    return new Float32Array(vertices)
  }

  private generateSpatialVertices(): Float32Array {
    const vertices: number[] = []

    // 3D spatial audio visualization
    for (let i = 0; i < 64; i++) {
      for (let j = 0; j < 64; j++) {
        const x = (i / 32) - 1
        const z = (j / 32) - 1
        const y = Math.sin(x * 5 + this.time) * Math.cos(z * 5 + this.time) * 0.5

        // Height-based coloring
        const height = (y + 1) / 2
        const r = height * 0.6 + 0.4
        const g = 0.3 + height * 0.4
        const b = 0.8 - height * 0.4

        vertices.push(x, y * 0.5, r, g, b, 1.0, i / 64, j / 64)
      }
    }

    return new Float32Array(vertices)
  }

  startRendering(mode: string): void {
    const renderLoop = () => {
      this.render(mode)
      this.animationFrameId = requestAnimationFrame(renderLoop)
    }
    renderLoop()
  }

  stopRendering(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  dispose(): void {
    this.stopRendering()
    if (this.gpuContext) {
      this.gpuContext.device.destroy()
    }
  }
}

const SWSWebGPUVisualizer: React.FC<SWSWebGPUVisualizerProps> = ({
  audioData,
  neuralActivity,
  frequencyData,
  width,
  height,
  mode = 'waveform',
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<WebGPUVisualizationEngine | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [currentMode, setCurrentMode] = useState(mode)
  const [isSupported, setIsSupported] = useState(true)

  const initializeWebGPU = useCallback(async () => {
    if (!canvasRef.current) return

    try {
      const engine = new WebGPUVisualizationEngine(canvasRef.current)
      await engine.initialize()
      engineRef.current = engine
      setIsInitialized(true)
      engine.startRendering(currentMode)
    } catch (error) {
      logger.warn('WebGPU not supported:', error)
      setIsSupported(false)
    }
  }, [currentMode])

  useEffect(() => {
    if (canvasRef.current && !isInitialized && isSupported) {
      initializeWebGPU()
    }

    return () => {
      if (engineRef.current) {
        engineRef.current.dispose()
      }
    }
  }, [initializeWebGPU, isInitialized, isSupported])

  useEffect(() => {
    if (engineRef.current) {
      engineRef.current.updateData(audioData, neuralActivity, frequencyData)
    }
  }, [audioData, neuralActivity, frequencyData])

  useEffect(() => {
    if (engineRef.current && currentMode !== mode) {
      setCurrentMode(mode)
      // Restart rendering with new mode
      engineRef.current.stopRendering()
      engineRef.current.startRendering(mode)
    }
  }, [mode, currentMode])

  const modes = [
    { id: 'waveform', name: 'Waveform', icon: '📊' },
    { id: 'spectrum', name: 'Spectrum', icon: '🎵' },
    { id: 'neural', name: 'Neural Net', icon: '🧠' },
    { id: 'particles', name: 'Particles', icon: '✨' },
    { id: 'spatial', name: '3D Spatial', icon: '🌌' }
  ]

  return (
    <div className={`sws-webgpu-visualizer ${className}`}>
      <div className="bg-black/90 backdrop-blur-md rounded-xl p-4 border border-cyan-500/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-cyan-400">WebGPU Visualizer</h3>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isInitialized ? 'bg-green-400' : isSupported ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
            <span className="text-xs text-cyan-300/60">
              {isSupported ? (isInitialized ? 'GPU Active' : 'Initializing...') : 'WebGPU Not Supported'}
            </span>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="flex flex-wrap gap-2 mb-4">
          {modes.map((modeOption) => (
            <button
              key={modeOption.id}
              onClick={() => setCurrentMode(modeOption.id as typeof currentMode)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                currentMode === modeOption.id
                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30'
                  : 'bg-cyan-900/30 text-cyan-300 hover:bg-cyan-800/50'
              }`}
              disabled={!isInitialized}
            >
              {modeOption.icon} {modeOption.name}
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="w-full h-auto border border-cyan-500/20 rounded-lg bg-black"
            style={{ aspectRatio: `${width}/${height}` }}
          />

          {!isSupported && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg">
              <div className="text-center text-cyan-300/60">
                <div className="text-lg mb-2">🚫 WebGPU Not Supported</div>
                <div className="text-sm">{`This browser doesn't support WebGPU acceleration`}</div>
                <div className="text-xs mt-2">Try Chrome 113+ or Edge 113+</div>
              </div>
            </div>
          )}

          {isSupported && !isInitialized && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg">
              <div className="text-center text-cyan-400">
                <div className="text-lg mb-2">⚡ Initializing WebGPU...</div>
                <div className="text-sm">Setting up GPU acceleration</div>
              </div>
            </div>
          )}
        </div>

        {/* Performance Info */}
        <div className="mt-4 text-xs text-cyan-400/40 text-center">
          <p>🎨 WebGPU Accelerated • Real-time Rendering • Neural Visualization</p>
        </div>
      </div>
    </div>
  )
}

export default SWSWebGPUVisualizer